import { Button, Text } from '@mercai/clever'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useParams } from 'react-router-dom'

import {
  onConfirmWarningMessage,
  onErrorMessage,
  onSuccessMessage,
  trackEvent,
  useParamsState,
  useValidationErrors,
} from '@/helpers'

import { useAuth } from '@/providers'

import { ROUTES_ADMIN } from '@/router'

import {
  QUERIES_ADMIN,
  useCalculateMargin,
  useCreateQuotation,
  useDollarQuotationLatest,
  useQuotationRequestDetail,
  useSplitQuotationRequest,
  useUpdateQuotationRequest,
} from '@/services'

import { AiQualificationCard } from '@/components'

import {
  QuotationRequestProducts,
  RfqStatusSelect,
  UpdateRfq,
} from './components'

import {
  AddressDrawerForm,
  BuyerDrawerForm,
  CatalogProducts,
  GeneralInformation,
  LogisticData,
  OrderData,
  QuotationDialog,
  QuotationMetrics,
  QuotationsModal,
} from '../../components'

import { getQuotationMetricsPayload } from '../../helpers'

import {
  checkIsCatalogProductsPrices,
  checkIsCompleteGeneralInformation,
  checkIsCompleteLogisticData,
  checkIsCompleteOrderData,
  schemaQuotation,
} from '../../quotation-validation'

import {
  accordionsByStatusObject,
  getQuotationRequestWithOrdenedItems,
} from './show.constants'

import styles from './show.module.css'

import {
  AddressProps,
  CalculateMarginPayloadProps,
  FinanceProfilePaymentFormsEnum,
  FinanceProfileTransactionPaymentMethodsEnum,
  ProfileProps,
  QuotationProps,
  QuotationRequestItemProps,
  QuotationRequestProps,
  QuotationRequestSplitEnum,
  QuotationRequestStatusEnum,
} from '@/types'

export const QuotationRequestV2Show = () => {
  const { t } = useTranslation('screenBusinessV2')

  const location = useLocation()
  const page = location.pathname.split('/').pop()

  const { uuid } = useParams()

  const queryClient = useQueryClient()

  const { mutateAsync: mutateAsyncCreateQuotation } = useCreateQuotation()

  const { data: dataDollarQuotationLatest } = useDollarQuotationLatest()

  const { whoAmI } = useAuth()

  const [quotationValues, setQuotationValues] = useState<
    Partial<QuotationProps>
  >({} as Partial<QuotationProps>)

  const [hasPulling, setHasPulling] = useState(false)

  const { data: quotationRequest, isLoading: isLoadingRequest } =
    useQuotationRequestDetail(uuid, hasPulling ? 3000 : undefined)

  const { mutateAsync: mutateAsyncUpdateQuotationRequest, isPending } =
    useUpdateQuotationRequest(uuid || '')

  const [quotationRequestOrdened, setQuotationRequestOrdened] =
    useState<QuotationRequestProps>()

  const setBuyerProfile = (buyerProfile: ProfileProps) => {
    setQuotationRequestOrdened({
      ...quotationRequestOrdened,
      buyerProfile,
      buyerProfileUuid: buyerProfile.uuid,
    } as QuotationRequestProps)
  }

  const setAddress = (address: Partial<AddressProps>) => {
    setQuotationRequestOrdened({
      ...quotationRequestOrdened,
      address,
      addressUuid: address.uuid,
    } as QuotationRequestProps)
  }

  const {
    mutateAsync: mutateAsyncSplitQuotationRequest,
    isPending: isPendingSplitQuotationRequest,
  } = useSplitQuotationRequest(uuid || '')

  const setToQualified = async () => {
    const response = await mutateAsyncUpdateQuotationRequest({
      ...quotationRequestOrdened,
      status: 'qualified',
    })

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.QUOTATION_REQUEST.module],
      })
    }
  }

  const isCompleteGeneralInformation = checkIsCompleteGeneralInformation({
    address: quotationRequestOrdened?.address,
    buyerProfile: quotationRequestOrdened?.buyerProfile,
    paymentFormOption: quotationValues?.paymentFormOption,
    financingCost: quotationValues?.financingCost,
    creditTerm: quotationValues?.creditTerm,
  })

  const isCompleteOrderData = checkIsCompleteOrderData({
    expiresAt: quotationValues?.expiresAt,
    expectedDeliveryDays: quotationValues?.expectedDeliveryDays,
    totalFreightPrice: quotationValues?.totalFreightPrice,
    purchaseOnCredit: quotationValues?.purchaseOnCredit,
    financingCost: quotationValues?.financingCost,
  })

  const isCompleteLogisticData = checkIsCompleteLogisticData({
    firstMileFreightCost: quotationValues?.firstMileFreightCost,
    lastMileFreightCost: quotationValues?.lastMileFreightCost,
    customsFreightCost: quotationValues?.customsFreightCost,
  })

  const isCompleteCatalogProductsPrices = checkIsCatalogProductsPrices(
    quotationValues?.products,
  )

  const isApprovedForQuotation =
    quotationRequestOrdened?.status === 'approved_for_quotation'

  const isQualified = quotationRequestOrdened?.status === 'qualified'

  const isProcessed = quotationRequestOrdened?.status === 'processed'
  const isCreatedRfq = quotationRequestOrdened?.status === 'created'

  const hasQuotation =
    quotationRequestOrdened?.quotations &&
    quotationRequestOrdened?.quotations?.length > 0

  const [quotationDialogIsOpen, setQuotationDialogIsOpen] = useState(false)

  const [dataForMetrics, setDataForMetrics] =
    useState<CalculateMarginPayloadProps>()

  const { data: dataCalculateMargin, mutateAsync: mutateAsyncCalculateMargin } =
    useCalculateMargin()

  const incomplete = useMemo(
    () => !dataCalculateMargin?.success,
    [dataCalculateMargin],
  )

  const [queryParams, setQueryParams] = useParamsState({
    form: '',
  })

  const [quotationsModalIsOpen, setQuotationsModalIsOpen] = useState(false)

  const onHandleOpenQuotationsModal = () => {
    if (quotationRequestOrdened?.quotations?.length === 0) return

    setQuotationsModalIsOpen(true)
  }

  const { isValid, errors, onHandleListenErrors } = useValidationErrors(
    schemaQuotation(t),
    quotationValues,
  )

  const onCloseDrawerForm = () => {
    setQueryParams({ ...queryParams, form: '' })
  }

  const getMetrics = (quotation: QuotationProps | undefined = undefined) => {
    const data = quotation || quotationValues
    if (data) {
      setDataForMetrics(
        getQuotationMetricsPayload({
          quotationValues: data as QuotationProps,
        }),
      )
    }
  }

  const accordionsComponents = {
    'general-information': (position: number) => (
      <GeneralInformation
        position={position}
        key="general-information"
        isComplete={isCompleteGeneralInformation}
        isLoading={isLoadingRequest}
        buyerProfile={quotationRequestOrdened?.buyerProfile}
        quotationValues={quotationValues}
        setQuotationValues={setQuotationValues}
        address={quotationRequestOrdened?.address}
        deliveryOption={quotationRequestOrdened?.deliveryOption}
        onHandleBuyerDrawerOpen={() =>
          setQueryParams({ ...queryParams, form: 'buyer' })
        }
        onHandleAddressDrawerOpen={() =>
          setQueryParams({ ...queryParams, form: 'address' })
        }
        isEditable={isApprovedForQuotation}
        getMetrics={getMetrics}
        errors={errors}
        quotationRequestStatus={quotationRequestOrdened?.status || 'pending'}
        isRfq
        paymentFormOption={quotationRequestOrdened?.paymentFormOption}
        paymentMethodOption={quotationRequestOrdened?.paymentMethodOption}
        onOpenRfqEdit={() =>
          setQueryParams({ ...queryParams, form: 'update-rfq' })
        }
      />
    ),
    'order-data': (position: number) => (
      <OrderData
        position={position}
        key="order-data"
        isComplete={isCompleteOrderData}
        quotationValues={quotationValues}
        setQuotationValues={setQuotationValues}
        errors={errors}
        getMetrics={getMetrics}
      />
    ),
    'catalog-products': (position: number) => (
      <CatalogProducts
        position={position}
        key="catalog-products"
        validation={isCompleteCatalogProductsPrices}
        isEditable={isApprovedForQuotation}
        quotationValues={quotationValues}
        setQuotationValues={setQuotationValues}
        catalogProducts={quotationRequestOrdened?.items || []}
        onHandleCatalogProductsDrawerOpen={() => {}}
        getMetrics={() => getMetrics()}
        quotationRequest={quotationRequestOrdened}
        errors={errors}
        cm3Margin={dataCalculateMargin?.data?.cm3Margin}
        grossMargin={dataCalculateMargin?.data?.grossMargin}
        dollarQuotation={dataDollarQuotationLatest?.data?.value}
        isRfq
      />
    ),
    'logistic-data': (position: number) => (
      <LogisticData
        position={position}
        key="logistic-data"
        isComplete={isCompleteLogisticData}
        quotationValues={quotationValues}
        setQuotationValues={setQuotationValues}
        errors={errors}
        getMetrics={getMetrics}
      />
    ),
    'quotation-request-products': (position: number) => (
      <QuotationRequestProducts
        position={position}
        key="quotation-request-products"
        validation={isCompleteCatalogProductsPrices}
        isEditable={isApprovedForQuotation || isCreatedRfq}
        catalogProducts={quotationRequestOrdened?.items || []}
        quotationRequest={quotationRequestOrdened}
        errors={errors}
      />
    ),
  }

  const onHandleSubmitComplete = async () => {
    if (!isValid) {
      return onHandleListenErrors(true)
    }

    onHandleListenErrors(false)

    const confirm = await onConfirmWarningMessage(
      t('quotationCreateConfirmTitle'),
      t('quotationCreateConfirmMessage'),
      t('quotationCreateConfirmButtonCreate'),
      t('quotationCreateConfirmButtonCancel'),
    )

    if (!confirm) {
      return
    }

    const response = await mutateAsyncCreateQuotation({
      ...quotationValues,
      transportCompany: undefined,
    } as QuotationProps)

    if (response.success) {
      onSuccessMessage(
        t('quotationFormSuccessCreateTitle'),
        t('quotationFormSuccessCreateMessage'),
      )

      window.location.href = `${ROUTES_ADMIN.BUSINESS.QUOTATION.SHOW.fullPath(response?.data?.uuid || '')}`
    } else {
      onErrorMessage(
        t('quotationFormErrorCreateTitle'),
        t('quotationFormErrorCreateMessage'),
      )
    }
  }

  const onHandleSubmit = () => {
    if (
      quotationValues?.products &&
      quotationValues?.products?.filter((product) => product.catalogProductUuid)
        ?.length < quotationValues?.products?.length
    ) {
      return setQuotationDialogIsOpen(true)
    }

    onHandleSubmitComplete()
  }

  const onHandleSubmitUpdate = async ({
    action,
    selectedProducts,
    paymentFormOption,
    paymentMethodOption,
  }: {
    selectedProducts: QuotationRequestItemProps[]
    action: keyof typeof QuotationRequestSplitEnum | ''
    paymentFormOption?: keyof typeof FinanceProfilePaymentFormsEnum
    paymentMethodOption?: keyof typeof FinanceProfileTransactionPaymentMethodsEnum
  }) => {
    let response: {
      success: boolean
      message: string
    } = {
      success: false,
      message: '',
    }

    if (action === '') {
      response = await mutateAsyncUpdateQuotationRequest({
        ...quotationRequestOrdened,
        items: selectedProducts,
        paymentFormOption,
        paymentMethodOption,
      })
    } else {
      response = await mutateAsyncSplitQuotationRequest({
        action,
        selectedItems: selectedProducts.map((product) => product.uuid),
      })

      setQueryParams({ ...queryParams, form: '' })
    }

    if (response.success) {
      onSuccessMessage(
        t('quotationRequestFormUpdateTitle'),
        t('quotationRequestFormUpdateMessage'),
      )

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.QUOTATION_REQUEST.module],
      })
    } else {
      onErrorMessage(
        t('quotationRequestFormUpdateTitle'),
        t('quotationRequestFormUpdateMessage'),
      )
    }
  }

  useEffect(() => {
    setQuotationValues({
      discount: '0.00',
      ...quotationValues,
      status: 'pending',
      buyerProfileUuid: quotationRequestOrdened?.buyerProfile?.uuid,
      buyerProfile: quotationRequestOrdened?.buyerProfile,
      deliveryOption: quotationRequestOrdened?.deliveryOption,
      deliveryAddressUuid: quotationRequestOrdened?.address?.uuid,
      deliveryAddress: quotationRequestOrdened?.address as AddressProps,
      creatorEmail: whoAmI?.email,
      dollarExchangeRate: dataDollarQuotationLatest?.data?.value,
      includeIva: true,
      customsFreightCost: quotationValues?.customsFreightCost,
      products: quotationRequestOrdened?.items?.map((item, i) => ({
        ...item,
        ...quotationValues?.products?.[i],
        quantity: item?.quantity || 0,
        catalogProduct: item?.catalogProduct,
        catalogProductUuid: item?.catalogProduct?.uuid,
      })) as QuotationProps['products'],
      quotationRequestUuid: quotationRequestOrdened?.uuid,
      paymentFormOption: quotationRequestOrdened?.paymentFormOption,
      paymentMethodOption: quotationRequestOrdened?.paymentMethodOption,
    } as Partial<QuotationProps>)
  }, [dataDollarQuotationLatest, whoAmI, quotationRequestOrdened])

  useEffect(() => {
    setQuotationRequestOrdened(
      getQuotationRequestWithOrdenedItems(quotationRequest?.data),
    )

    if (
      quotationRequest?.data?.status === 'draft' ||
      quotationRequest?.data?.status === 'qualified'
    ) {
      setHasPulling(true)
    } else {
      setHasPulling(false)
    }
  }, [quotationRequest])

  useEffect(() => {
    trackEvent('adminRfqShowPageView', { page })
  }, [])

  useEffect(() => {
    if (
      dataForMetrics &&
      dataForMetrics?.customsFreightCost &&
      Object.keys(dataForMetrics).length > 0
    )
      mutateAsyncCalculateMargin(dataForMetrics)
  }, [dataForMetrics])

  const disableStatus = useMemo(() => {
    const hasNonCatalogedProducts = quotationValues?.products?.some(
      (product) => product.catalogProduct === null,
    )

    return (
      hasNonCatalogedProducts ||
      quotationRequestOrdened?.status === QuotationRequestStatusEnum.draft
    )
  }, [quotationRequestOrdened])

  if (queryParams.form === 'update-rfq') {
    return (
      <UpdateRfq
        isLoading={isPendingSplitQuotationRequest}
        onHandleSubmit={onHandleSubmitUpdate}
        items={quotationRequestOrdened?.items as QuotationRequestItemProps[]}
        reference={quotationRequestOrdened?.reference}
        onHandleClose={() => setQueryParams({ ...queryParams, form: '' })}
        paymentFormOption={quotationRequestOrdened?.paymentFormOption}
        paymentMethodOption={quotationRequestOrdened?.paymentMethodOption}
        buyerProfile={quotationRequestOrdened?.buyerProfile}
      />
    )
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <Text variant="text-small" className={styles.breadcrumb}>
          <Link
            to={ROUTES_ADMIN.BUSINESS.RFQ.fullPath()}
          >{`${t('quotationRequestTitle')}`}</Link>
          {` / `}
          <span>{quotationRequestOrdened?.reference}</span>
        </Text>

        <div className={styles['header-content']}>
          <div>
            <div className={styles['sub-title']}>
              <Text variant="title-large">
                {t(
                  quotationRequestOrdened?.items?.length === 1
                    ? 'quotationIdentifier'
                    : 'quotationIdentifierPlural',
                  {
                    rfq: quotationRequestOrdened?.reference,
                    quantity: quotationRequestOrdened?.items?.length,
                  },
                )}
              </Text>

              <RfqStatusSelect
                quotationRequest={quotationRequestOrdened}
                disabled={disableStatus}
              />

              {quotationRequestOrdened?.isSample && (
                <Text variant="title-large" className={styles['sample-title']}>
                  {t('quotationTitleIsSample')}
                </Text>
              )}
            </div>
            <Text
              variant="text-base"
              className={styles['sub-title-instructions']}
            >
              {t('instructionGenerateProposals')}
            </Text>
          </div>
          <div className={styles['header-actions']}>
            {!isProcessed && (
              <Button
                variant="secondary"
                onClick={() =>
                  setQueryParams({ ...queryParams, form: 'update-rfq' })
                }
              >
                {t('buttonEditRequestQuotation')}
              </Button>
            )}
            {!isProcessed && !isQualified && !isApprovedForQuotation && (
              <Button
                variant="secondary"
                onClick={setToQualified}
                disabled={isPending}
              >
                {t('buttonApproveRFQ')}
              </Button>
            )}
            {!isProcessed && isApprovedForQuotation && (
              <Button variant="primary" onClick={onHandleSubmit}>
                {t('buttonCreateQuotation')}
              </Button>
            )}
            {isProcessed && hasQuotation && (
              <Button variant="primary" onClick={onHandleOpenQuotationsModal}>
                {t('buttonViewQuotations')}
              </Button>
            )}
          </div>
        </div>

        <AiQualificationCard
          aiQualificationData={quotationRequestOrdened?.aiQualificationData}
          isLoading={isLoadingRequest}
        />
      </header>

      {isApprovedForQuotation && (
        <QuotationMetrics
          values={dataCalculateMargin?.data}
          incomplete={incomplete}
        />
      )}

      {accordionsByStatusObject[
        quotationRequestOrdened?.status || 'pending'
      ].map((key, i) =>
        accordionsComponents[key as keyof typeof accordionsComponents](i + 1),
      )}

      <BuyerDrawerForm
        uuid={quotationRequestOrdened?.buyerProfile?.uuid || ''}
        isOpen={queryParams.form === 'buyer'}
        onHandleOpen={onCloseDrawerForm}
        setBuyerProfile={setBuyerProfile}
        onSubmit={() => setQueryParams({ ...queryParams, form: 'address' })}
      />
      <AddressDrawerForm
        quotationRequest={quotationRequestOrdened as QuotationRequestProps}
        isOpen={queryParams.form === 'address'}
        onHandleOpen={onCloseDrawerForm}
        setAddress={setAddress}
        onBack={() => setQueryParams({ ...queryParams, form: 'buyer' })}
      />

      <QuotationDialog
        onHandleSubmit={onHandleSubmitComplete}
        isOpen={quotationDialogIsOpen}
        onHandleClose={() => setQuotationDialogIsOpen(false)}
      />

      {isProcessed && (
        <QuotationsModal
          quotations={quotationRequestOrdened?.quotations || []}
          isOpen={quotationsModalIsOpen}
          onHandleClose={() => setQuotationsModalIsOpen(false)}
        />
      )}
    </div>
  )
}
